import { Building2Icon, UsersIcon } from 'lucide-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { Button, DataTable, EmptyState, ErrorNotice, PageHeading, Pagination, Panel, SearchInput, SkeletonTable } from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { CONTACT_DEFAULT_SORT, CONTACT_SORT_FIELDS, CONTACT_SORT_KEYS, SALES_ACCOUNTS_PATH, listSalesContacts, pageCount } from '@/lib/sales-data';
import { formatDay, isNotAllowed } from '@/lib/sales-workspace';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import { AccountLink, ContactFlags, ContactLink } from '@/components/sales-workspace/sales-records';

/**
 * Every Salesforce contact, most recently active first: who they are, where
 * they work, how to reach them, and whether they may be emailed or called.
 *
 * Dark: the API answers 403 to anyone off the sales workspace allow-list.
 *
 * @returns The page.
 */
export function SalesContactsPage(): React.ReactElement {
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(CONTACT_SORT_KEYS, CONTACT_DEFAULT_SORT);
  const contacts = useQuery({
    queryKey: ['sales-contacts', debounced, sort, page],
    queryFn: ({ signal }) => listSalesContacts({ q: debounced, page, sort }, signal),
    placeholderData: keepPreviousData,
    retry: (count, error) => !isNotAllowed(error) && count < 2,
  });

  if (contacts.isError && isNotAllowed(contacts.error)) return <Navigate to="/events" replace />;

  const data = contacts.data;
  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="SALES"
        title="Contacts"
        subtitle="Everyone at our clients and prospects, with how to reach them and whether they may be."
        action={
          <Button variant="secondary" to={SALES_ACCOUNTS_PATH}>
            <Building2Icon aria-hidden className="size-4" /> Accounts
          </Button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search contacts"
          placeholder="Name, email or account"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
        {data === undefined ? null : (
          <span className="text-portal-micro text-portal-muted">
            {data.total} {data.total === 1 ? 'contact' : 'contacts'}
          </span>
        )}
      </div>

      {contacts.isError ? (
        <ErrorNotice title="Contacts unavailable" message={errorMessage(contacts.error)} />
      ) : contacts.isPending ? (
        <SkeletonTable label="Loading contacts…" />
      ) : contacts.data.rows.length === 0 ? (
        <Panel>
          {debounced ? (
            <EmptyState message="No contacts match that search." />
          ) : (
            <EmptyState icon={<UsersIcon aria-hidden className="size-6" />} title="No contacts yet" message="Contacts sync in from Salesforce every few minutes and appear here." />
          )}
        </Panel>
      ) : (
        <DataTable
          headers={[
            { label: 'Name', sortKey: CONTACT_SORT_FIELDS.name },
            { label: 'Account', sortKey: CONTACT_SORT_FIELDS.account },
            { label: 'Title', sortKey: CONTACT_SORT_FIELDS.title },
            'Email and phone',
            { label: 'Last activity', sortKey: CONTACT_SORT_FIELDS.lastActivity },
            'Reach out',
          ]}
          sort={sort}
          onSort={onSort}
          minWidth={960}
        >
          {contacts.data.rows.map((contact) => (
            <tr key={contact.id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <ContactLink id={contact.id} name={contact.name} className="font-bold text-brand hover:underline" />
              </td>
              <td className="p-3 text-portal-small">
                <AccountLink id={contact.accountId} name={contact.accountName} />
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{contact.title || '—'}</td>
              <td className="p-3 text-portal-small">
                <span className="block">{contact.email || '—'}</span>
                <span className="block text-portal-micro text-portal-muted">{contact.phone || 'No phone'}</span>
              </td>
              <td className="p-3 text-portal-small whitespace-nowrap text-portal-muted">{formatDay(contact.lastActivityDate, { withYear: true })}</td>
              <td className="p-3 text-portal-small">
                {contact.emailOptOut || contact.doNotCall ? <ContactFlags contact={contact} /> : <span className="text-portal-muted">—</span>}
              </td>
            </tr>
          ))}
        </DataTable>
      )}
      {data !== undefined && pageCount(data) > 1 ? (
        <Pagination
          page={page}
          totalPages={pageCount(data)}
          onChange={setPage}
          summary={`${(page - 1) * data.pageSize + 1}–${(page - 1) * data.pageSize + data.rows.length} of ${data.total}`}
        />
      ) : null}
    </section>
  );
}
